import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Loader from "../../Loader";
import "../addons.css";
import AuthContext from "../../../contexts/AuthProvider";

import { API_URL } from "../../../utils/config";
const baseUrl = API_URL;

const AddFacility = ({ onClose }) => {
  const { t } = useTranslation("facilty");
  const navigate = useNavigate();
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    tool_name: "",
    venue_id: "",
    type: "",
    cost: "",
    duration: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessages, setErrorMessages] = useState({});
  const { isAuthenticated } = useContext(AuthContext);
   const getLangFromLocalStorage = () => {
    return localStorage.getItem("language") || "en";
  };
  const fetchVenues = async () => {
    try {
      if (!isAuthenticated) {
        navigate("/");
        return [];
      }
      const res = await fetch(`${baseUrl}/api/v1/attnd-venue/assignedVenues`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          lang: getLangFromLocalStorage(),
        },
      });
      if (res.status === 401) {
        navigate("/login");
        throw new Error(t("addFacility.errors.unauthorized"));
      }
      if (!res.ok) {
        throw new Error(t("addFacility.errors.fetchFailed"));
      }
      const json = await res.json();
      return json.data;
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("token");
         
        navigate("/login");
      }
      return [];
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      const data = await fetchVenues();
      setVenues(data);
      setLoading(false);
    })();
  }, []);

  const validateField = (name, value) => {
    switch (name) {
      case "tool_name":
        return value
          ? ""
          : t("addFacility.errors.toolNameRequired");
      case "venue_id":
        return value
          ? ""
          : t("addFacility.errors.venueRequired");
      case "type":
        return value
          ? ""
          : t("addFacility.errors.typeRequired");
      case "cost":
        if (!value) return t("addFacility.errors.costRequired");
        if (parseFloat(value) <= 0) return t("addFacility.errors.costPositive");
        return "";
      case "duration":
        return value
          ? ""
          : t("addFacility.errors.durationRequired");
      default:
        return "";
    }
  };

  const isFormValid = () =>
    Object.keys(formData).every(
      (field) => validateField(field, formData[field]) === ""
    );

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "cost" && parseFloat(value) < 0) {
      setErrorMessages((prev) => ({
        ...prev,
        [name]: t("addFacility.errors.costPositive"),
      }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrorMessages((prev) => ({
      ...prev,
      [name]: validateField(name, value) || undefined,
    }));
  };

  const handleKeyDown = (e) => {
    if (e.key === "-") e.preventDefault();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const currentErrors = Object.keys(formData).reduce((errs, field) => {
      const err = validateField(field, formData[field]);
      if (err) errs[field] = err;
      return errs;
    }, {});
    if (Object.keys(currentErrors).length) {
      setErrorMessages(currentErrors);
      setIsSubmitting(false);
      return;
    }

    try {
      if (!isAuthenticated) {
        navigate("/");
        return;
      }
      const payload = {
        tool_name: formData.tool_name,
        venue_id: Number(formData.venue_id),
        type: formData.type,
        cost: parseFloat(formData.cost),
        duration: formData.duration,
      };
     
      const res = await fetch(`${baseUrl}/api/v1/attnd-venue/addFacility`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          lang: getLangFromLocalStorage(),
        },
        body: JSON.stringify(payload),
      });
      if (res.status === 401) {
        navigate("/login");
        throw new Error(t("addFacility.errors.unauthorized"));
      }
      if (!res.ok) {
        const errJson = await res.json();
        if (errJson.errors) {
          const backendErrors = errJson.errors.reduce((acc, err) => {
            const field = Object.keys(err)[0];
            acc[field] = err[field];
            return acc;
          }, {});
          setErrorMessages(backendErrors);
        } else {
          throw new Error(
            errJson.error || t("addFacility.errors.addFailed")
          );
        }
        return;
      }
      await res.json();
      onClose();
    } catch (err) {
      console.error(err);
      setErrorMessages({ message: err.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="invite-attendee-modal">
      <div className="form-invite-attendee">
        <form onSubmit={handleSubmit} noValidate>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                {t("addFacility.labels.toolName")}:
              </label>
              <input
                type="text"
                name="tool_name"
                className="form-input"
                placeholder={t("addFacility.placeholders.toolName")}
                value={formData.tool_name}
                onChange={handleChange}
                required
              />
              {errorMessages.tool_name && (
                <span className="error-messages">
                  {errorMessages.tool_name}
                </span>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                {t("addFacility.labels.venue")}:
              </label>
              <div className="form-select-container">
                <select
                  name="venue_id"
                  className="form-select"
                  value={formData.venue_id}
                  onChange={handleChange}
                  disabled={loading}
                  required
                >
                  <option value="">
                    {t("addFacility.placeholders.selectVenue")}
                  </option>
                  {venues.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name}
                    </option>
                  ))}
                </select>
              </div>
              {errorMessages.venue_id && (
                <span className="error-messages">
                  {errorMessages.venue_id}
                </span>
              )}
            </div>
          </div>

          {loading && <Loader />}

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                {t("addFacility.labels.type")}:
              </label>
              <div className="form-select-container">
                <select
                  name="type"
                  className="form-select"
                  value={formData.type}
                  onChange={handleChange}
                  required
                >
                  <option value="">
                    {t("addFacility.placeholders.selectType")}
                  </option>
                  {Object.entries(
                    t("addFacility.options.type", { returnObjects: true })
                  ).map(([val, label]) => (
                    <option key={val} value={val}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
              {errorMessages.type && (
                <span className="error-messages">{errorMessages.type}</span>
              )}
            </div>
          </div>

          <div className="form-row" style={{ margin: 0 }}>
            <div className="form-group">
              <label className="form-label">
                {t("addFacility.labels.cost")}:
              </label>
              <input
                type="number"
                name="cost"
                className="form-input"
                placeholder={t("addFacility.placeholders.cost")}
                value={formData.cost}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                required
              />
              {errorMessages.cost && (
                <span className="error-messages">
                  {errorMessages.cost}
                </span>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                {t("addFacility.labels.duration")}:
              </label>
              <div className="form-select-container">
                <select
                  name="duration"
                  className="form-select"
                  value={formData.duration}
                  onChange={handleChange}
                  required
                >
                  <option value="">
                    {t("addFacility.placeholders.selectDuration")}
                  </option>
                  {Object.entries(
                    t("addFacility.options.duration", { returnObjects: true })
                  ).map(([val, label]) => (
                    <option key={val} value={val}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
              {errorMessages.duration && (
                <span className="error-messages">
                  {errorMessages.duration}
                </span>
              )}
            </div>
          </div>

          {errorMessages.message && (
            <p className="error-messages" style={{ marginBottom: "15px" }}>
              {errorMessages.message}
            </p>
          )}

          <div className="modal-footer">
            <button
              type="button"
              className="cancel-button"
              onClick={onClose}
              disabled={isSubmitting}
            >
              {t("addFacility.buttons.cancel")}
            </button>
            <button
              type="submit"
              className="invite-button"
              disabled={isSubmitting || !isFormValid()}
            >
              {isSubmitting
                ? t("addFacility.buttons.submitting")
                : t("addFacility.buttons.submit")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddFacility;
